import type { ReactElement, FunctionComponent, SVGProps } from 'react';

import { post } from 'utils/network';
import { ApiRoute, FeatureFlag, PaymentUpdateFunnel } from 'config';
import { supportsApplePay, runsIos, runsSafari, runsWebview } from 'utils/device';
import HeapAnalytics from 'services/HeapAnalytics';

import useCurrentUser from 'hooks/useCurrentUser';

import { Component as ApplePayIcon } from 'payment-img/apple-pay/apple-pay-card-icon.svg';
import { Component as GooglePayIcon } from 'payment-img/google-pay/google-pay-card-icon.svg';
import { Component as PayPalIcon } from 'payment-img/paypal/paypal-card-icon.svg';
import { Component as CreditCardIcon } from 'payment-img/credit-card/credit-card-icon.svg';
import { Component as VenmoIcon } from 'payment-img/venmo/venmo-card-icon.svg';

import CreditCardProvider from './CreditCardProvider';
import PayPalProvider from './PayPalProvider';
import GooglePayProvider from './GooglePayProvider';
import ApplePayProvider from './ApplePayProvider';
import VenmoProvider from './VenmoProvider';

enum PaymentProvider {
  CREDIT_CARD,
  PAYPAL,
  APPLE_PAY,
  GOOGLE_PAY,
  VENMO,
}

enum UI {
  BUTTONS,
  TABS,
}

type ButtonProps = {
  ui: UI;
  text: string;
  icon: FunctionComponent<SVGProps<SVGElement>>;
  provider: PaymentProvider;
  activeByDefault?: boolean;
  selectedProvider: PaymentProvider;
  setSelectedProvider: (provider: PaymentProvider) => void;
};

function ProviderButton({
  ui,
  text,
  icon: Icon,
  provider,
  activeByDefault = false,
  selectedProvider,
  setSelectedProvider,
}: ButtonProps): ReactElement {
  return (
    <button
      type="button"
      id="payment-button-cc"
      aria-controls="payment-method-cc"
      className={[
        'c-payment-form__head',
        selectedProvider === provider
        || (activeByDefault && selectedProvider === null)
          ? 'is-active'
          : '',
        selectedProvider === null || selectedProvider === provider
        || ui === UI.TABS
          ? ''
          : 'd-none',
      ].join(' ')}
      aria-expanded={selectedProvider === provider}
      onClick={() => setSelectedProvider(provider)}
    >
      {ui === UI.BUTTONS && <Icon height={20} />}
      <span>{text}</span>
    </button>
  );
}

type ViewProps = {
  ui?: UI;
  ccNumber: string;
  expMonth: string;
  expYear: string;
  cvc: string;
  error: string;
  selectedProvider: PaymentProvider;
  setCCNumber: (ccNumber: string) => void;
  setExpMonth: (expMonth: string) => void;
  setExpYear: (expYear: string) => void;
  setCvc: (cvc: string) => void;
  setSelectedProvider: (provider: PaymentProvider | null) => void;
  onAdd?: () => void;
  onSuccess?: () => void;
};

function PaymentView({
  ui = UI.BUTTONS,
  ccNumber,
  expMonth,
  expYear,
  cvc,
  error,
  selectedProvider,
  setCCNumber,
  setExpMonth,
  setExpYear,
  setCvc,
  setSelectedProvider,
  onAdd = () => null,
  onSuccess = () => null,
}: ViewProps): ReactElement {
  const { currentUser } = useCurrentUser();

  const addPaymentMethod = async (providerName, token) => {
    const paymentUpdateFunnel = (currentUser.registered_at
      ? PaymentUpdateFunnel.USER_PROFILE
      : PaymentUpdateFunnel.STANDARD_SIGNUP
    );

    HeapAnalytics.trackSuccessfullyAuthPayment(
      paymentUpdateFunnel,
      providerName,
    );

    onAdd();

    await post(ApiRoute.PAYMENT_METHOD, {
      user: {
        payment_method_attributes: {
          token,
          provider_name: providerName,
        },
      },
    });

    await onSuccess();
  };

  return (
    <>
      <div
        className={[
          'c-payment-form',
          ui === UI.BUTTONS ? '' : 'c-payment-form--tabs',
        ].join(' ')}
      >
        <div className="c-payment-form__buttons">
          <ProviderButton
            ui={ui}
            text="Credit Card"
            icon={CreditCardIcon}
            provider={PaymentProvider.CREDIT_CARD}
            activeByDefault
            selectedProvider={selectedProvider}
            setSelectedProvider={setSelectedProvider}
          />

          <ProviderButton
            ui={ui}
            text="PayPal"
            icon={PayPalIcon}
            provider={PaymentProvider.PAYPAL}
            selectedProvider={selectedProvider}
            setSelectedProvider={setSelectedProvider}
          />

          {supportsApplePay
          && currentUser.hasFeatureEnabled(FeatureFlag.APPLE_PAY) && (
            <ProviderButton
              ui={ui}
              text="Apple Pay"
              icon={ApplePayIcon}
              provider={PaymentProvider.APPLE_PAY}
              selectedProvider={selectedProvider}
              setSelectedProvider={setSelectedProvider}
            />
          )}

          {currentUser.hasFeatureEnabled(FeatureFlag.GOOGLE_PAY) && (
            <ProviderButton
              ui={ui}
              text="Google Pay"
              icon={GooglePayIcon}
              provider={PaymentProvider.GOOGLE_PAY}
              selectedProvider={selectedProvider}
              setSelectedProvider={setSelectedProvider}
            />
          )}

          {runsIos && runsSafari && !runsWebview && (
            <ProviderButton
              ui={ui}
              text="Venmo"
              icon={VenmoIcon}
              provider={PaymentProvider.VENMO}
              selectedProvider={selectedProvider}
              setSelectedProvider={setSelectedProvider}
            />
          )}
        </div>

        <CreditCardProvider
          show={
            selectedProvider === PaymentProvider.CREDIT_CARD
            || (ui === UI.TABS && selectedProvider === null)
          }
          error={error}
          number={ccNumber}
          expMonth={expMonth}
          expYear={expYear}
          cvc={cvc}
          setNumber={setCCNumber}
          setExpMonth={setExpMonth}
          setExpYear={setExpYear}
          setCvc={setCvc}
        />

        <PayPalProvider
          show={selectedProvider === PaymentProvider.PAYPAL}
          onToken={addPaymentMethod}
        />

        {supportsApplePay
        && currentUser.hasFeatureEnabled(FeatureFlag.APPLE_PAY) && (
          <ApplePayProvider
            show={selectedProvider === PaymentProvider.APPLE_PAY}
            onToken={addPaymentMethod}
          />
        )}

        {currentUser.hasFeatureEnabled(FeatureFlag.GOOGLE_PAY) && (
          <GooglePayProvider
            show={selectedProvider === PaymentProvider.GOOGLE_PAY}
            onToken={addPaymentMethod}
          />
        )}

        {runsIos && runsSafari && !runsWebview && (
          <VenmoProvider
            show={selectedProvider === PaymentProvider.VENMO}
            onToken={addPaymentMethod}
          />
        )}
      </div>

      {ui === UI.BUTTONS && (
        <button
          type="button"
          className={[
            'c-payment-form__footer',
            selectedProvider !== null ? '' : 'd-none',
          ].join(' ')}
          onClick={() => setSelectedProvider(null)}
        >
          Choose another way to pay
        </button>
      )}
    </>
  );
}

export { PaymentProvider, UI };
export default PaymentView;
